import type { Card } from '@braze/web-sdk';
import { datadogRum } from '@datadog/browser-rum';
import { mapBrazeCardToItem, sortNotificationItems } from './mapBrazeCard';
import type { NotificationCenterItem } from './types';

const REFRESH_THROTTLE_MS = 60_000;

type ContentCardsListener = (state: ContentCardsState) => void;

export type ContentCardsState = {
	items: NotificationCenterItem[];
	isLoading: boolean;
	hasError: boolean;
	hasLoaded: boolean;
};

let brazeSdkPromise: Promise<typeof import('@braze/web-sdk')> | null = null;
let subscriptionId: string | null = null;
/** Serializes concurrent ensureSubscription callers so only one Braze listener is created. */
let subscriptionPromise: Promise<void> | null = null;
let cardsById = new Map<string, Card>();
let locallyViewedIds = new Set<string>();
/** Optimistic dismissals until Braze cache catches up; cleared on reset. */
let locallyDismissedIds = new Set<string>();
let lastRefreshAt = 0;
/** Bumped on reset so in-flight refresh/subscription callbacks are ignored. */
let sessionGeneration = 0;
/** In-flight refresh; concurrent callers await this instead of starting a rival attempt. */
let refreshInFlight: Promise<void> | null = null;
let state: ContentCardsState = {
	items: [],
	isLoading: false,
	hasError: false,
	hasLoaded: false,
};
const listeners = new Set<ContentCardsListener>();

function logContentCardsError(action: string, error: unknown): void {
	datadogRum.addAction(
		action,
		error instanceof Error ? { message: error.message } : { error }
	);
}

type BrazeSdk = typeof import('@braze/web-sdk');

function markCardReadLocally(id: string): void {
	locallyViewedIds.add(id);
	setState({
		items: state.items.map((item) =>
			item.id === id ? { ...item, isRead: true } : item
		),
	});
}

function removeCardLocally(id: string): void {
	locallyDismissedIds.add(id);
	cardsById.delete(id);
	setState({
		items: state.items.filter((item) => item.id !== id),
	});
}

async function runContentCardAction(
	id: string,
	errorAction: string,
	applyLocalChange: () => void,
	brazeAction: (braze: BrazeSdk, card: Card) => void
): Promise<void> {
	const card = cardsById.get(id);
	if (!card) {
		return;
	}

	applyLocalChange();

	try {
		const braze = await getBrazeSdk();
		brazeAction(braze, card);
	} catch (error) {
		logContentCardsError(errorAction, error);
	}
}

const getBrazeSdk = () => {
	if (process.env.NODE_ENV === 'test') {
		return Promise.reject(new Error('Braze SDK disabled in tests'));
	}
	if (!brazeSdkPromise) {
		brazeSdkPromise = import('@braze/web-sdk');
	}
	return brazeSdkPromise;
};

function emit() {
	listeners.forEach((listener) => listener(state));
}

function setState(partial: Partial<ContentCardsState>) {
	state = { ...state, ...partial };
	emit();
}

function applyCards(cards: Card[], generation: number) {
	if (generation !== sessionGeneration) {
		return;
	}

	cardsById = new Map();
	const mapped: NotificationCenterItem[] = [];

	for (const card of cards) {
		if (!card?.id || card.isControl || locallyDismissedIds.has(card.id)) {
			continue;
		}
		cardsById.set(card.id, card);
		const item = mapBrazeCardToItem(card);
		if (!item) {
			continue;
		}
		mapped.push({
			...item,
			isRead: item.isRead || locallyViewedIds.has(item.id),
		});
	}

	setState({
		items: sortNotificationItems(mapped),
		isLoading: false,
		hasError: false,
		hasLoaded: true,
	});
}

async function ensureSubscription(generation: number) {
	if (subscriptionId || process.env.NODE_ENV === 'test') {
		return;
	}

	if (!subscriptionPromise) {
		subscriptionPromise = (async () => {
			try {
				const braze = await getBrazeSdk();
				if (generation !== sessionGeneration || subscriptionId) {
					return;
				}
				subscriptionId =
					braze.subscribeToContentCardsUpdates((contentCards) => {
						applyCards(contentCards?.cards ?? [], generation);
					}) ?? null;
			} catch (error) {
				logContentCardsError(
					'NotificationCenter_ContentCardsSubscriptionFailed',
					error
				);
			}
		})().finally(() => {
			if (generation === sessionGeneration) {
				subscriptionPromise = null;
			}
		});
	}

	await subscriptionPromise;
}

/**
 * Subscribe to Braze Content Cards updates.
 * Prefer calling via `refreshContentCards` from flag-gated React hooks so users
 * outside `web-notification-center-enabled` do not pull cards.
 */
export async function registerContentCardsSubscription(): Promise<void> {
	await ensureSubscription(sessionGeneration);
}

export function resetContentCardsService(): void {
	sessionGeneration += 1;
	const previousSubscriptionId = subscriptionId;
	subscriptionId = null;
	subscriptionPromise = null;
	refreshInFlight = null;
	cardsById = new Map();
	locallyViewedIds = new Set();
	locallyDismissedIds = new Set();
	lastRefreshAt = 0;
	state = {
		items: [],
		isLoading: false,
		hasError: false,
		hasLoaded: false,
	};
	emit();

	if (previousSubscriptionId && process.env.NODE_ENV !== 'test') {
		void getBrazeSdk()
			.then((braze) => {
				braze.removeSubscription(previousSubscriptionId);
			})
			.catch((error) => {
				logContentCardsError('NotificationCenter_ContentCardsUnsubscribeFailed', error);
			});
	}
}

export function getContentCardsState(): ContentCardsState {
	return state;
}

export function getUnreadContentCardCount(): number {
	return state.items.filter((item) => !item.isRead).length;
}

export function subscribeToContentCardsState(
	listener: ContentCardsListener
): () => void {
	listeners.add(listener);
	listener(state);
	return () => {
		listeners.delete(listener);
	};
}

export async function refreshContentCards(force = false): Promise<void> {
	// Share one in-flight refresh so drawer→screen does not abort a live attempt
	// and then no-op on throttle, leaving isLoading stuck.
	if (refreshInFlight) {
		await refreshInFlight;
		if (!force) {
			return;
		}
	}

	const generation = sessionGeneration;
	const now = Date.now();
	if (!force && lastRefreshAt && now - lastRefreshAt < REFRESH_THROTTLE_MS) {
		return;
	}

	const run = (async () => {
		setState({
			isLoading: !state.hasLoaded || state.hasError,
			hasError: false,
		});
		lastRefreshAt = Date.now();

		try {
			await ensureSubscription(generation);
			if (generation !== sessionGeneration) {
				return;
			}

			const braze = await getBrazeSdk();
			if (generation !== sessionGeneration) {
				return;
			}

			const cached = braze.getCachedContentCards();
			if (cached?.cards) {
				applyCards(cached.cards, generation);
			}

			await new Promise<void>((resolve, reject) => {
				braze.requestContentCardsRefresh(
					() => {
						if (generation !== sessionGeneration) {
							resolve();
							return;
						}
						const refreshed = braze.getCachedContentCards();
						applyCards(refreshed?.cards ?? [], generation);
						const mapped = getContentCardsState().items;
						// Empty result may mean Braze session is not ready yet — allow
						// an immediate follow-up refresh instead of a 60s empty lockout.
						if (mapped.length === 0) {
							lastRefreshAt = 0;
						}
						datadogRum.addAction('NotificationCenter_ContentCardsRefresh', {
							rawCount: refreshed?.cards?.length ?? 0,
							mappedCount: mapped.length,
							pinnedCount: mapped.filter((item) => item.isPinned).length,
							unreadCount: mapped.filter((item) => !item.isRead).length,
							thumbnailCount: mapped.filter(
								(item) => item.imageLayout === 'thumbnail'
							).length,
							heroCount: mapped.filter((item) => item.imageLayout === 'hero')
								.length,
						});
						resolve();
					},
					() => reject(new Error('Content Cards refresh failed'))
				);
			});
		} catch (error) {
			if (generation !== sessionGeneration) {
				return;
			}
			lastRefreshAt = 0;
			logContentCardsError('NotificationCenter_ContentCardsRefreshFailed', error);
			setState({
				isLoading: false,
				hasError: true,
				hasLoaded: false,
			});
		}
	})();

	refreshInFlight = run.finally(() => {
		if (refreshInFlight === run) {
			refreshInFlight = null;
		}
	});
	await refreshInFlight;
}

export async function logContentCardImpression(id: string): Promise<void> {
	await runContentCardAction(
		id,
		'NotificationCenter_ContentCardImpressionFailed',
		() => markCardReadLocally(id),
		(braze, card) => braze.logContentCardImpressions([card])
	);
}

export async function logContentCardClick(id: string): Promise<void> {
	await runContentCardAction(
		id,
		'NotificationCenter_ContentCardClickFailed',
		() => markCardReadLocally(id),
		(braze, card) => braze.logContentCardClick(card)
	);
}

export async function dismissContentCard(id: string): Promise<void> {
	await runContentCardAction(
		id,
		'NotificationCenter_ContentCardDismissFailed',
		() => removeCardLocally(id),
		(braze, card) => braze.logCardDismissal(card)
	);
}

/** Test-only helper to reset module state between Jest cases. */
export function __resetContentCardsServiceForTests() {
	resetContentCardsService();
	listeners.clear();
}
